import { camelCase, mean, isNil } from 'lodash-es'
import { coerceNumberOrTrimmedString } from '@/utils/formatters'

import { buildColumnDef, ERROR_DISPLAY_VALUE } from '@/components/shared-table/buildColumnDef'
import { buildMappedGroup } from '@/components/combined-predict/components/CombinedPredictTable/utils/mappedGroupBuilders'
import { applyDefaultChildrenIfNoChildren } from '@/components/combined-predict/components/CombinedPredictTable/utils/mappedGroupBuilders'
import { parsePrimaryKeyAndSource } from '@/components/shared-table/utils'
import { formatScientificWithBlankEmpty, formatRange } from '@/components/combined-predict/utils'
import { shouldHideField, compareValuesNullsLast } from '@/components/shared-table/utils'

const buildGroupIdByName = (name) => `${camelCase(name)}Group`

const aggregationColumns = [
  {
    id: 'average',
    name: 'Average',
    // shortName: 'Avg.',
    // className: 'border border-top-0 border-bottom-0 border-right-0 border-info',
  },
  {
    id: 'median',
    name: 'Median',
    // shortName: 'Med.'
  },
  {
    id: 'ranges',
    name: 'Ranges',
    // shortName: 'Rang.',
    // className: 'border border-top-0 border-bottom-0 border-left-0 border-info',
  },
]

const buildGroupHeader = (groupName, unit) => ({
  id: buildGroupIdByName(groupName),
  name: groupName,
  unit,
})

const buildGroup = (groupName, unit) => {
  const group = {
    ...buildGroupHeader(groupName, unit),
    children: aggregationColumns.map((column) => applyDefaultChildrenIfNoChildren(column)),
  }
  return buildMappedGroup(group)
}

const filterVisibleMemberIds = (memberIds, visibleColumnIds) =>
  memberIds?.length ? memberIds?.filter((id) => visibleColumnIds?.includes(id)) : []

const getAggregatedDisplayValue = (aggregationContext) => {
  const { collectedValues = [], aggregationType } = aggregationContext || {}

  if (!collectedValues?.length || !aggregationType) return ''

  if (collectedValues?.length === 1) return collectedValues[0]

  const coercedValues = collectedValues.map(coerceNumberOrTrimmedString).filter((value) => !isNil(value))

  const calculateAggregatedValue = (type) => {
    if (type === 'average') return mean(coercedValues)

    if (type === 'median') {
      const sorted = [...coercedValues].sort((a, b) => a - b)
      const mid = Math.floor(sorted.length / 2)
      return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
    }
    if (type === 'ranges') {
      const min = Math.min(...coercedValues)
      const max = Math.max(...coercedValues)
      return [min, max]
    }
    return ''
  }

  const aggregatedValue = calculateAggregatedValue(aggregationType)

  // return formatRange(aggregatedValue)

  return Array.isArray(aggregatedValue) ? formatRange(aggregatedValue) : formatScientificWithBlankEmpty(aggregatedValue)
}

const buildAggregationContextFromColumn = ({ columnId, groupId, memberIds }) => {
  const { primaryKey: aggregationType, sourceType } = parsePrimaryKeyAndSource(columnId, groupId)
  if (!aggregationType || !sourceType) return null

  const getMemberIdsForSourceType = (sourceType) =>
    memberIds.filter((id) => {
      const { sourceType: memberSourceType } = parsePrimaryKeyAndSource(id)
      return memberSourceType === sourceType
    })

  return {
    aggregationType,
    memberIds: getMemberIdsForSourceType(sourceType),
  }
}

const collectValuesByColumnIds = ({ ids, getCellValue, node }) =>
  ids
    .map((id) => getCellValue({ colKey: id, rowNode: node, useFormatter: true }))
    .map((value) => {
      return isNil(value) || value === '' || value === ERROR_DISPLAY_VALUE ? null : value
    })
    .filter((value) => !isNil(value))

const buildAggregateColumnDef = ({ column, visibleColumnIds, tableSettings }) => {
  const visibleMemberIds = filterVisibleMemberIds(column?.memberIds, visibleColumnIds)

  const { aggregationType, memberIds: sameSourceTypeMemberIds } = buildAggregationContextFromColumn({
    columnId: column?.id,
    groupId: column?.groupId,
    memberIds: visibleMemberIds,
  })

  return buildColumnDef({
    column,
    visibleColumnIds,
    tableSettings,
    hide: sameSourceTypeMemberIds?.length < 2 || shouldHideField({ fieldKey: column?.id, tableSettings }),
    customCellClasses: ['font-italic'],

    valueGetter: (params) => {
      const collectedValues = collectValuesByColumnIds({
        ids: sameSourceTypeMemberIds,
        getCellValue: params?.api?.getCellValue?.bind(params?.api),
        node: params.node,
      })

      // if (params?.colDef?.field?.startsWith('waterSolubilityGroup'))
      //   console.log('waterSolubilityGroup', collectedValues)
      return { aggregationType, collectedValues }
    },

    tooltipValueGetter: (params) =>
      params?.value?.collectedValues?.length ? params?.value?.collectedValues?.join(', ') : undefined,

    valueFormatter: (params) => getAggregatedDisplayValue(params?.value),

    comparator: (aValue, bValue, _nodeA, _nodeB, isDescending) => {
      const rawAValue = getAggregatedDisplayValue(aValue)
      const rawBValue = getAggregatedDisplayValue(bValue)
      return compareValuesNullsLast(rawAValue, rawBValue, isDescending)
    },
  })
}

export { buildGroup, buildAggregateColumnDef }
